import type { components } from '../../types/api.generated';
import { API_ENDPOINTS } from '../config/constants';
import { apiFetch } from './common';
import { userApiClient } from './client';
import { logger } from '../utils/logger';

// Type aliases from generated API types
export type SubscriptionTier = components['schemas']['SubscriptionTierResponse'];
export type UsageSummary = components['schemas']['UsageSummaryResponse'];
export type UsageStatsItem = components['schemas']['UsageStatsItemResponse'];
export type TierInfo = components['schemas']['TierInfoResponse'];

/**
 * Subscription API methods
 */
export const SubscriptionsAPI = {
  /**
   * Get the current organization's subscription tier
   */
  async getCurrentTier(signal?: AbortSignal): Promise<SubscriptionTier> {
    return apiFetch<SubscriptionTier>(API_ENDPOINTS.SUBSCRIPTIONS.CURRENT, {
      signal,
    });
  },

  /**
   * Get usage summary for the current organization
   */
  async getUsageSummary(signal?: AbortSignal): Promise<UsageSummary> {
    return apiFetch<UsageSummary>(API_ENDPOINTS.SUBSCRIPTIONS.USAGE, {
      signal,
    });
  },

  /**
   * List all available subscription tiers
   */
  async listTiers(signal?: AbortSignal): Promise<SubscriptionTier[]> {
    return apiFetch<SubscriptionTier[]>(API_ENDPOINTS.SUBSCRIPTIONS.TIERS, {
      signal,
    });
  },

  /**
   * Create a Stripe checkout session for subscription upgrade.
   *
   * `options.successUrl` / `options.cancelUrl` are optional overrides for the
   * Stripe redirect URLs. The backend validates them to be on the same origin
   * as the configured frontend app URL — useful when callers (e.g. the
   * onboarding wizard) want Stripe to return the user to a custom page rather
   * than the default `/billing/success` route.
   *
   * Stripe substitutes `{CHECKOUT_SESSION_ID}` in `successUrl` at redirect
   * time, so callers may include that literal token verbatim.
   */
  async createCheckoutSession(
    tierSlug: string,
    interval: 'monthly' | 'yearly',
    options?: { successUrl?: string; cancelUrl?: string },
  ): Promise<{ checkoutUrl: string }> {
    return userApiClient.post<{ checkoutUrl: string }>(
      API_ENDPOINTS.SUBSCRIPTIONS.CREATE_CHECKOUT_SESSION,
      {
        tierSlug,
        interval,
        ...(options?.successUrl !== undefined && { successUrl: options.successUrl }),
        ...(options?.cancelUrl !== undefined && { cancelUrl: options.cancelUrl }),
      },
    );
  },

  /**
   * Create a Stripe customer portal session for subscription management
   */
  async createPortalSession(): Promise<{ portalUrl: string }> {
    return userApiClient.post<{ portalUrl: string }>(
      API_ENDPOINTS.SUBSCRIPTIONS.CREATE_PORTAL_SESSION,
    );
  },

  /**
   * Cancel the organization's subscription
   */
  async cancelSubscription(
    atPeriodEnd: boolean = true,
  ): Promise<{ status: string; cancelAt: string | null }> {
    return userApiClient.post<{ status: string; cancelAt: string | null }>(
      API_ENDPOINTS.SUBSCRIPTIONS.CANCEL,
      { atPeriodEnd },
    );
  },

  /**
   * Schedule the organization's existing subscription to move to a different
   * tier at the end of the current billing period.
   *
   * Unlike checkout, this changes the current subscription rather than creating
   * a new one, so the org actually lands on the target plan. Used by the
   * downgrade flow: the current tier stays in effect until the paid period
   * ends, then the target tier applies at the next cycle. Rejected with 409
   * `USAGE_EXCEEDS_TIER` when current usage exceeds the target tier's limits.
   *
   * `interval` is optional: when omitted the backend preserves the
   * subscription's current billing cadence, which is what a downgrade wants.
   */
  async changePlan(
    tierSlug: string,
    interval?: 'monthly' | 'yearly',
  ): Promise<{ status: string; tierSlug: string | null }> {
    return userApiClient.post<{ status: string; tierSlug: string | null }>(
      API_ENDPOINTS.SUBSCRIPTIONS.CHANGE_PLAN,
      { tierSlug, ...(interval !== undefined && { interval }) },
    );
  },

  /**
   * Sync customer information from Stripe
   */
  async syncCustomerInfo(): Promise<void> {
    try {
      await userApiClient.post(API_ENDPOINTS.SUBSCRIPTIONS.SYNC_CUSTOMER_INFO);
    } catch (error) {
      // Fire-and-forget - log but don't throw
      logger.warn('Failed to sync customer info from Stripe', error);
    }
  },

  /**
   * Sync subscription data from Stripe to local database
   * Use this when subscription doesn't update after checkout or webhook fails
   */
  async syncSubscription(): Promise<{
    status: string;
    tier?: string;
    subscriptionStatus?: string;
    reason?: string;
  }> {
    return userApiClient.post<{
      status: string;
      tier?: string;
      subscriptionStatus?: string;
      reason?: string;
    }>(API_ENDPOINTS.SUBSCRIPTIONS.SYNC_SUBSCRIPTION);
  },

  /**
   * Attempt to reconcile invalid subscription state.
   * Called automatically when the backend detects customer_id but no subscription_id
   * while status claims active. Queries Stripe to find and link the subscription.
   */
  async reconcileSubscription(): Promise<{
    status: string;
    subscriptionId?: string;
    tier?: string;
    subscriptionStatus?: string;
    message: string;
  }> {
    return userApiClient.post<{
      status: string;
      subscriptionId?: string;
      tier?: string;
      subscriptionStatus?: string;
      message: string;
    }>(API_ENDPOINTS.SUBSCRIPTIONS.RECONCILE);
  },
};
